import React from 'react';
import {
    Box,
    Card,
    Stack,
    Typography,
    Avatar,
    CircularProgress,
    Fade,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

interface ProgressOverlayProps {
    isLoading: boolean;
    isSuccess: boolean;
    isVisible: boolean;
    loadingTitle: string;
    successTitle: string;
    loadingMessage?: string;
    successMessage?: string;
    icon: React.ReactNode;
    loadingColor?: string;
    successColor?: string;
    position?: 'absolute' | 'fixed';
}

const ProgressOverlay: React.FC<ProgressOverlayProps> = ({
    isLoading,
    isSuccess,
    isVisible,
    loadingTitle,
    successTitle,
    loadingMessage,
    successMessage,
    icon,
    loadingColor = '#3b82f6',
    successColor = '#10b981',
    position = 'absolute'
}) => {
    if (!isVisible) return null;

    return (
        <Fade in={isVisible} timeout={300}>
            <Box sx={{
                position: position,
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                borderRadius: position === 'absolute' ? 3 : 0
            }}>
                <Card sx={{
                    p: 5,
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                    background: 'white',
                    border: isSuccess
                        ? `1px solid rgba(16, 185, 129, 0.2)`
                        : `1px solid rgba(59, 130, 246, 0.1)`,
                    minWidth: 280,
                    maxWidth: 400
                }}>
                    <Stack alignItems="center" spacing={3}>
                        <Box sx={{ position: 'relative' }}>
                            {isLoading ? (
                                <>
                                    <CircularProgress
                                        size={60}
                                        thickness={4}
                                        sx={{
                                            color: loadingColor,
                                            '& .MuiCircularProgress-circle': {
                                                strokeLinecap: 'round',
                                            }
                                        }}
                                    />
                                    <Avatar sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        background: `linear-gradient(135deg, ${loadingColor} 0%, #1d4ed8 100%)`,
                                        width: 32,
                                        height: 32
                                    }}>
                                        {icon}
                                    </Avatar>
                                </>
                            ) : (
                                <Avatar sx={{
                                    background: `linear-gradient(135deg, ${successColor} 0%, #059669 100%)`,
                                    width: 60,
                                    height: 60,
                                    animation: 'pulse 1.5s ease-in-out',
                                    '@keyframes pulse': {
                                        '0%': { transform: 'scale(1)' },
                                        '50%': { transform: 'scale(1.1)' },
                                        '100%': { transform: 'scale(1)' }
                                    }
                                }}>
                                    <CheckCircle sx={{ fontSize: 32, color: 'white' }} />
                                </Avatar>
                            )}
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" sx={{
                                color: isSuccess ? '#059669' : '#374151',
                                fontWeight: 600,
                                mb: 1
                            }}>
                                {isSuccess ? successTitle : loadingTitle}
                            </Typography>
                            {(loadingMessage || successMessage) && (
                                <Typography variant="body2" sx={{
                                    color: '#64748b',
                                    lineHeight: 1.5
                                }}>
                                    {isSuccess ? successMessage : loadingMessage}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Card>
            </Box>
        </Fade>
    );
};

export default ProgressOverlay;
